import { useState, useEffect, useRef, useCallback } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Save, Loader2, Edit } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  getAIProviderCredentials,
  createAIProvider,
  updateAIProvider,
} from "@/services/aiProvidersService";
import type {
  AIProvider,
  ProviderAuthData,
  ProviderAuthConfig,
} from "@/types/aiProviders";

interface AIProviderFormProps {
  onBack: () => void;
  onSave: () => void;
  editingProvider: AIProvider | null;
}

interface FormData {
  name: string;
  provider_id: string;
  description: string;
  is_active: boolean;
}

export const AIProviderForm = ({
  onBack,
  onSave,
  editingProvider,
}: AIProviderFormProps) => {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    provider_id: "",
    description: "",
    is_active: true,
  });
  const [providersAuthData, setProvidersAuthData] =
    useState<ProviderAuthData | null>(null);
  const [currentProviderConfig, setCurrentProviderConfig] =
    useState<ProviderAuthConfig | null>(null);
  const [authConfigValues, setAuthConfigValues] = useState<
    Record<string, string>
  >({});
  const [originalAuthValues, setOriginalAuthValues] = useState<
    Record<string, string>
  >({});
  const [editingFields, setEditingFields] = useState<Record<string, boolean>>(
    {}
  );
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const loadingRef = useRef(false);

  /**
   * Mascara o valor de uma chave de autenticação
   * Mostra apenas os últimos 4 caracteres (ou 2 se menor que 4)
   */
  const maskValue = (value: string): string => {
    if (!value || value.length === 0) return "";

    // Se o valor for muito curto (1-2 chars), mostrar tudo
    if (value.length <= 2) return value;

    // Para valores >= 3, decidir quantos caracteres mostrar
    const visibleChars = value.length >= 4 ? 4 : 2;
    const maskedLength = Math.max(0, value.length - visibleChars);
    const maskedPart = "•".repeat(maskedLength);
    const visiblePart = value.slice(-visibleChars);

    return maskedPart + visiblePart;
  };

  /**
   * Manipula o foco no campo de autenticação
   * Ao clicar, limpa o valor e muda para modo texto
   */
  const handleFieldFocus = (envVar: string) => {
    setEditingFields({
      ...editingFields,
      [envVar]: true,
    });
    setAuthConfigValues({
      ...authConfigValues,
      [envVar]: "",
    });
  };

  const loadProvidersAuthData = async () => {
    try {
      const response = await fetch("/data/providers_auth.json");
      const data: ProviderAuthData = await response.json();
      return data;
    } catch (error) {
      toast({
        title: "Erro ao carregar configurações",
        description:
          "Não foi possível carregar as configurações de autenticação",
        variant: "destructive",
      });
      return null;
    }
  };

  const loadFormData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await loadProvidersAuthData();
      setProvidersAuthData(data);

      if (editingProvider) {
        setFormData({
          name: editingProvider.name,
          provider_id: editingProvider.provider_id || "",
          description: editingProvider.description || "",
          is_active: editingProvider.is_active,
        });

        // Carregar configuração deste provedor
        if (data) {
          // Buscar usando múltiplos critérios em ordem de prioridade
          let config = null;

          // 1. Tentar por provider_id (mais confiável)
          if (editingProvider.provider_id) {
            config = data.providers.find(
              (p) => p.provider_id.toLowerCase() === editingProvider.provider_id?.toLowerCase()
            );
          }

          // 2. Tentar por display_name (case-insensitive)
          if (!config) {
            config = data.providers.find(
              (p) => p.display_name.toLowerCase() === editingProvider.name.toLowerCase()
            );
          }

          // 3. Tentar por provider_id usando o name (caso o name seja o slug)
          if (!config) {
            config = data.providers.find(
              (p) => p.provider_id.toLowerCase() === editingProvider.name.toLowerCase()
            );
          }

          setCurrentProviderConfig(config || null);

          // Carregar credenciais
          const credsResponse = await getAIProviderCredentials(
            editingProvider.id
          );

          if (credsResponse.success && credsResponse.data) {
            const decryptedCreds = credsResponse.data.auth_config;

            // SEGURANÇA: Carregar valores MASCARADOS no state
            // Os valores reais são mantidos em originalAuthValues apenas para referência
            if (config) {
              const envVars = config.env_vars
                .split(",")
                .map((v) => v.trim().replace("?", ""));

              const maskedValues: Record<string, string> = {};
              const realValues: Record<string, string> = {};

              envVars.forEach((envVar) => {
                // Verificar se decryptedCreds é um objeto válido
                let realValue: string | undefined = undefined;

                // Suportar diferentes formatos: objeto direto ou string JSON
                if (decryptedCreds) {
                  if (typeof decryptedCreds === "string") {
                    try {
                      const parsed = JSON.parse(decryptedCreds);
                      realValue = parsed?.[envVar];
                    } catch (e) {
                      // Se não conseguir fazer parse, tratar como objeto vazio
                      realValue = undefined;
                    }
                  } else if (typeof decryptedCreds === "object") {
                    realValue = decryptedCreds[envVar];
                  }
                }

                // Converter para string se necessário e garantir que não seja null/undefined
                // Valores vazios são permitidos (string vazia "")
                const finalValue = realValue != null ? String(realValue) : "";

                // Armazena valor real em originalAuthValues (uso interno)
                realValues[envVar] = finalValue;
                // Armazena valor mascarado em authConfigValues (exibição)
                // Se valor vazio, não mascara (mostra vazio)
                maskedValues[envVar] = finalValue ? maskValue(finalValue) : "";
              });

              setOriginalAuthValues(realValues);
              setAuthConfigValues(maskedValues);
            }
          }
        }
      } else {
        // Novo provedor - resetar
        setFormData({
          name: "",
          provider_id: "",
          description: "",
          is_active: true,
        });
        setCurrentProviderConfig(null);
        setAuthConfigValues({});
        setOriginalAuthValues({});
        setEditingFields({});
      }
    } catch (error) {
      toast({
        title: "Erro ao carregar dados",
        description:
          error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [editingProvider]);

  useEffect(() => {
    // Evitar execuções duplicadas em StrictMode
    if (loadingRef.current) {
      return;
    }

    loadingRef.current = true;
    loadFormData().finally(() => {
      loadingRef.current = false;
    });
  }, [loadFormData]);

  const handleProviderIdChange = (providerId: string) => {
    setFormData({ ...formData, provider_id: providerId });

    if (providersAuthData && providerId) {
      const config = providersAuthData.providers.find(
        (p) => p.provider_id === providerId
      );
      setCurrentProviderConfig(config || null);

      // Resetar valores de autenticação
      if (config) {
        const initialValues: Record<string, string> = {};
        config.env_vars
          .split(",")
          .map((v) => v.trim().replace("?", ""))
          .forEach((envVar) => {
            initialValues[envVar] = "";
          });
        setAuthConfigValues(initialValues);
        setOriginalAuthValues({});
        setEditingFields({});
      }
    }
  };

  const handleSaveProvider = async () => {
    if (!formData.name.trim()) {
      toast({
        title: "Campo obrigatório",
        description: "O nome é obrigatório",
        variant: "destructive",
      });
      return;
    }

    if (!formData.provider_id.trim()) {
      toast({
        title: "Campo obrigatório",
        description: "Selecione o tipo de provedor",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      // Construir auth_config APENAS com valores realmente alterados
      // Não incluir valores mascarados não editados para evitar salvamento incorreto
      const finalAuthConfig: Record<string, string> = {};

      // Usar todas as chaves de authConfigValues (valores atuais) ou originalAuthValues (valores originais)
      const allKeys = new Set([
        ...Object.keys(authConfigValues),
        ...Object.keys(originalAuthValues),
      ]);

      allKeys.forEach((key) => {
        const currentValue = authConfigValues[key];
        const wasEdited = editingFields[key] === true;
        const originalValue = originalAuthValues[key];

        // Só incluir se o campo foi realmente editado
        if (wasEdited) {
          // Usar o novo valor digitado (pode ser vazio)
          finalAuthConfig[key] = currentValue != null ? String(currentValue).trim() : "";
        } else {
          // Verificar se o valor atual é diferente do original (não mascarado)
          const currentValueStr = currentValue != null ? String(currentValue || "").trim() : "";
          const originalValueStr = originalValue != null ? String(originalValue || "").trim() : "";
          const maskedOriginal = originalValue ? maskValue(originalValue) : "";

          // Só incluir se:
          // 1. O valor atual é diferente do original (não é apenas mascarado)
          // 2. E o valor atual não é igual ao valor mascarado do original
          if (currentValueStr !== originalValueStr && currentValueStr !== maskedOriginal) {
            finalAuthConfig[key] = currentValueStr;
          }
          // Se não foi editado e o valor é igual ao original ou mascarado, NÃO incluir
          // O backend manterá o valor atual no banco
        }
      });

      // Preparar dados do provedor
      const providerData: any = {
        name: formData.name,
        provider_id: formData.provider_id,
        description: formData.description,
        is_active: formData.is_active,
      };

      // Só incluir auth_config se houver valores realmente alterados
      // Se finalAuthConfig estiver vazio, não enviar (backend manterá valores atuais)
      if (Object.keys(finalAuthConfig).length > 0) {
        providerData.auth_config = finalAuthConfig;
      }

      if (editingProvider) {
        const response = await updateAIProvider(
          editingProvider.id,
          providerData
        );

        if (response.success) {
          toast({
            title: "Sucesso",
            description: "Provedor atualizado com sucesso",
          });
          onSave();
        } else {
          throw new Error(
            response.error?.message || "Erro ao atualizar provedor"
          );
        }
      } else {
        const response = await createAIProvider(providerData);

        if (response.success) {
          toast({
            title: "Sucesso",
            description: "Provedor criado com sucesso",
          });
          onSave();
        } else {
          throw new Error(response.error?.message || "Erro ao criar provedor");
        }
      }
    } catch (error) {
      toast({
        title: "Erro",
        description:
          error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const toggleEditField = (fieldName: string) => {
    setEditingFields({
      ...editingFields,
      [fieldName]: !editingFields[fieldName],
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Carregando formulário...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted">
      <PageHeader
        title={editingProvider ? "Editar Provedor" : "Novo Provedor de IA"}
        description={
          editingProvider
            ? "Atualize as informações do provedor"
            : "Adicione um novo provedor de IA ao sistema"
        }
        onBack={onBack}
        backDisabled={saving}
        actions={
          <Button
            onClick={handleSaveProvider}
            disabled={saving}
            className="bg-gradient-primary text-white hover:opacity-90"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Salvar
              </>
            )}
          </Button>
        }
      />

      <main className="container mx-auto p-6">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>Informações Básicas</CardTitle>
              <CardDescription>
                Configure os dados principais do provedor
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-2">
                <Label htmlFor="name">Nome *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="Ex: Google Production, OpenAI Dev, etc."
                  disabled={saving}
                />
                <p className="text-xs text-muted-foreground">
                  Nome personalizado para identificar esta configuração
                </p>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="provider_id">Tipo de Provedor *</Label>
                {!editingProvider && providersAuthData ? (
                  <select
                    id="provider_id"
                    value={formData.provider_id}
                    onChange={(e) => handleProviderIdChange(e.target.value)}
                    disabled={saving}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                  >
                    <option value="">Selecione o tipo de provedor</option>
                    {providersAuthData.providers
                      .filter((p) => p.active === "true")
                      .map((provider) => (
                        <option key={provider.provider_id} value={provider.provider_id}>
                          {provider.display_name}
                        </option>
                      ))}
                  </select>
                ) : (
                  <>
                    <Input
                      id="provider_id"
                      value={
                        providersAuthData?.providers.find(
                          (p) => p.provider_id === formData.provider_id
                        )?.display_name || formData.provider_id
                      }
                      disabled={true}
                      className="bg-muted cursor-not-allowed"
                    />
                    <p className="text-xs text-muted-foreground">
                      O tipo de provedor não pode ser alterado
                    </p>
                  </>
                )}
              </div>

              <div className="grid gap-2">
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      description: e.target.value,
                    })
                  }
                  placeholder="Breve descrição do provedor"
                  rows={4}
                  disabled={saving}
                />
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-0.5">
                  <Label
                    htmlFor="is_active"
                    className="text-base cursor-pointer"
                  >
                    Provedor Ativo
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {formData.is_active
                      ? "O provedor está ativo e disponível para uso"
                      : "O provedor está inativo e não pode ser utilizado"}
                  </p>
                </div>
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, is_active: checked })
                  }
                  disabled={saving}
                />
              </div>
            </CardContent>
          </Card>

          {/* Authentication Configuration */}
          {currentProviderConfig && (
            <Card>
              <CardHeader>
                <CardTitle>Configuração de Autenticação</CardTitle>
                <CardDescription>
                  Configure as variáveis de ambiente para autenticação
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Categorias */}
                {currentProviderConfig.categories && (
                  <div>
                    <Label className="text-sm font-medium mb-2 block">
                      Categorias
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {currentProviderConfig.categories
                        .split(",")
                        .map((category) => category.trim())
                        .map((category) => (
                          <Badge key={category} variant="secondary">
                            {category}
                          </Badge>
                        ))}
                    </div>
                  </div>
                )}

                {/* Notas */}
                {currentProviderConfig.notes && (
                  <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-md p-3">
                    <p className="text-sm text-blue-900 dark:text-blue-100">
                      <strong>Nota:</strong> {currentProviderConfig.notes}
                    </p>
                  </div>
                )}

                {/* Variáveis de Ambiente */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium">
                    Variáveis de Ambiente
                  </Label>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-1/3">Variável</TableHead>
                          <TableHead>Valor</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {currentProviderConfig.env_vars
                          .split(",")
                          .map((envVar) => envVar.trim().replace("?", ""))
                          .map((envVar) => {
                            const originalValue = originalAuthValues[envVar];
                            const hasOriginalValue =
                              originalValue && originalValue.trim().length > 0;
                            const isEditing = editingFields[envVar] === true;
                            const currentValue = String(
                              authConfigValues[envVar] || ""
                            );
                            const hasCurrentValue = currentValue.trim().length > 0;

                            // Se tem valor original mas não está editando, mostrar mascarado
                            // Se está editando ou não tem valor, mostrar o que o usuário digitou
                            const displayValue = hasOriginalValue && !isEditing
                              ? maskValue(originalValue)
                              : currentValue;

                            return (
                              <TableRow key={envVar}>
                                <TableCell className="font-mono text-sm font-medium">
                                  {envVar}
                                </TableCell>
                                <TableCell>
                                  <div className="space-y-2">
                                    <Input
                                      type="text"
                                      value={displayValue}
                                      onChange={(e) => {
                                        // Quando o usuário digita, marcar como editado e atualizar o valor
                                        if (!isEditing) {
                                          setEditingFields({
                                            ...editingFields,
                                            [envVar]: true,
                                          });
                                        }
                                        setAuthConfigValues({
                                          ...authConfigValues,
                                          [envVar]: e.target.value,
                                        });
                                      }}
                                      onFocus={() => {
                                        if (hasOriginalValue && !isEditing) {
                                          handleFieldFocus(envVar);
                                        } else if (!isEditing) {
                                          // Marcar como editando quando focar em campo vazio
                                          setEditingFields({
                                            ...editingFields,
                                            [envVar]: true,
                                          });
                                        }
                                      }}
                                      placeholder={`Digite o valor para ${envVar}`}
                                      className="font-mono text-sm"
                                      disabled={saving}
                                      readOnly={hasOriginalValue && !isEditing}
                                    />
                                    {hasOriginalValue && !isEditing && (
                                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                        <span className="font-mono">
                                          Valor configurado: {maskValue(originalValue)}
                                        </span>
                                        <Badge
                                          variant="outline"
                                          className="text-xs"
                                        >
                                          Configurado
                                        </Badge>
                                      </div>
                                    )}
                                    {isEditing && (
                                      <div className="text-xs text-amber-600 dark:text-amber-400">
                                        Digite o novo valor (o anterior será
                                        substituído)
                                      </div>
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
};

